import React, { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import Card from '../../SystemDesign/Card';
import FormInput from '../../SystemDesign/FormInput';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import logo from "../../Assets/logo.svg";
import { useResetPasswordMutation } from '../../services/SkillsManagement';
import { useSearchParams, useNavigate } from 'react-router-dom';

const ResetPassword = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchParams] = useSearchParams();
  const [resetPassword] = useResetPasswordMutation();
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const navigate = useNavigate();
  
  // State for password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .min(8, 'Password must contain at least 8 characters')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[!@#$%^&*]/, 'Password must contain at least one special character')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
      .required('Password confirmation is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const token = searchParams.get('token');
      if (!token) {
        setSnackbar({
          open: true,
          message: 'Reset token is missing',
          severity: 'error'
        });
        return;
      }

      // Structure avec body pour RTK Query
      const response = await resetPassword({
        body: {
          token: token,
          newPassword: values.newPassword
        }
      }).unwrap();

      setSnackbar({
        open: true,
        message: 'Password changed successfully',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error) {
      console.error('Error details:', error);
      setSnackbar({
        open: true,
        message: error.data?.message || 'Error changing password',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
      }}
    >

      {/* Main Container */}
      <Box sx={{ 
        width: '100%', 
        maxWidth: '500px',
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* Logo */}
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            width: '100px',
            height: 'auto',
            mb: 1.5,
          }}
        />

        {/* Welcome Text Container */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mb: 2.5
        }}>
          {/* Bienvenue */}
          <Typography
            variant="h4"
            sx={{
              color: colors.primary[800],
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '2rem',
              fontFamily: 'Akatab, sans-serif',
              mb: 0.5
            }}
          >
            Welcome!
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="subtitle1"
            sx={{
              color: colors.grey[500],
              textAlign: 'center',
              fontSize: '0.95rem',
            }}
          >
            You can change your password
          </Typography>
        </Box>

        {/* Password Change Card */}
        <Card 
          title="Change Password"
          titleStyle={{ 
            fontSize: '1.1rem',
            fontWeight: 500
          }}
        >
          <Box sx={{ mt: 2 }}>
            <Formik
              initialValues={{
                newPassword: '',
                confirmPassword: '',
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isValid, dirty }) => (
                <Form>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2.5
                  }}>
                    <FormInput
                      name="newPassword"
                      label="New password"
                      type={showNewPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={toggleNewPasswordVisibility}
                            edge="end"
                            size="small"
                            sx={{ 
                              color: colors.grey[400],
                              '&:hover': {
                                color: colors.primary[400]
                              }
                            }}
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                    />
                    
                    <FormInput
                      name="confirmPassword"
                      label="Confirm password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={toggleConfirmPasswordVisibility}
                            edge="end"
                            size="small"
                            sx={{ 
                              color: colors.grey[400],
                              '&:hover': {
                                color: colors.primary[400]
                              }
                            }}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                    />

                    {/* Password Requirements */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.grey[500],
                        backgroundColor: colors.grey[100],
                        p: 2,
                        borderRadius: 1,
                      }}
                    >
                      Password must contain:
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>At least 8 characters</li>
                        <li>At least one uppercase letter</li>
                        <li>At least one number</li>
                        <li>At least one special character (!@#$%^&*)</li>
                      </ul>
                    </Typography>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isValid || !dirty}
                      sx={{
                        mt: 2,
                        backgroundColor: colors.primary[400],
                        '&:hover': {
                          backgroundColor: colors.primary[500],
                        },
                        '&:disabled': {
                          backgroundColor: colors.grey[300],
                        },
                      }}
                    >
                      Change Password
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPassword;